"use client";

import {
  type CSSProperties,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useState,
} from "react";
import SearchBar from "@/components/assets/input/searchBar";
import { DetailHeader } from "@/components/layout/detail/detailHeader";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";

type MasterDetailLayoutProps = {
  leftSidebar: ReactNode;
  leftSidebarWidth?: string;
  detailView: ReactNode;
  emptyStateMessage?: string;
  showSearchBar?: boolean;
  toggleLabel: string;
  headerActions?: ReactNode;
  defaultActions?: boolean;
  defaultActionsMenuItems?: Array<{
    label: string;
    onClick?: () => void;
  }>;
  showDetail?: boolean;
  initialLeftSidebarOpen?: boolean;
};

/**
 * Master-detail layout component with configurable left sidebar, center detail view,
 * and attached right sidebar (handled by detailView component).
 *
 * Coordinates left and right sidebar state - when right sidebar opens, left sidebar closes (fullscreen mode).
 * When left sidebar opens and detail is showing, right sidebar closes.
 *
 * Usage:
 * <MasterDetailLayout
 *   leftSidebar={<JobSidebar ... />}
 *   detailView={<JobDetail ... />}
 *   toggleLabel="Job"
 *   showDetail={true}
 * />
 */
export function MasterDetailLayout({
  leftSidebar,
  leftSidebarWidth = "400px",
  detailView,
  emptyStateMessage = "Select an item from the sidebar to view details",
  showSearchBar = true,
  toggleLabel,
  headerActions,
  defaultActions = true,
  defaultActionsMenuItems,
  showDetail = true,
  initialLeftSidebarOpen = true,
}: MasterDetailLayoutProps) {
  // Control left sidebar
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(
    initialLeftSidebarOpen
  );
  // Control right sidebar (managed by detailView component, but we coordinate)
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  // Handler for left sidebar - when it opens, close right sidebar if detail is showing
  const handleLeftSidebarChange = (open: boolean) => {
    setLeftSidebarOpen(open);
    // If left sidebar opens and detail is showing, close right sidebar
    if (open && showDetail) {
      setRightSidebarOpen(false);
    }
  };

  // Handler for right sidebar - when it opens, close left sidebar (fullscreen mode)
  const handleRightSidebarChange = (open: boolean) => {
    setRightSidebarOpen(open);
    // If right sidebar opens, close left sidebar (fullscreen mode)
    if (open) {
      setLeftSidebarOpen(false);
    }
  };

  // Clone detailView to inject right sidebar state props
  const detailViewWithProps = isValidElement(detailView)
    ? cloneElement(
        detailView as ReactElement<{
          rightSidebarOpen?: boolean;
          onRightSidebarChange?: (open: boolean) => void;
        }>,
        {
          rightSidebarOpen,
          onRightSidebarChange: handleRightSidebarChange,
        }
      )
    : detailView;

  return (
    <>
      {showSearchBar && (
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SearchBar />
        </div>
      )}
      <div className="relative overflow-hidden">
        {/* Left sidebar provider */}
        <SidebarProvider
          onOpenChange={handleLeftSidebarChange}
          open={leftSidebarOpen}
          style={
            {
              "--sidebar-width": leftSidebarWidth,
            } as CSSProperties
          }
        >
          <div
            className={`[&_[data-slot='sidebar-container']]:!absolute [&_[data-slot='sidebar-container']]:!h-full [&_[data-slot='sidebar-container']]:!left-0 [&_[data-slot='sidebar-container']]:!top-0 [&_[data-slot='sidebar-container']]:!transition-all ${leftSidebarOpen ? "" : '[&_[data-slot="sidebar-container"]]:!-left-[350px] [&_[data-slot="sidebar-container"]]:!opacity-0 [&_[data-slot="sidebar-container"]]:!pointer-events-none'}`}
          >
            {leftSidebar}
          </div>
          <SidebarInset
            className="flex h-full min-w-0 flex-1 flex-col transition-all duration-200 ease-linear"
            style={{
              marginLeft: leftSidebarOpen ? "10px" : "0px",
            }}
          >
            <DetailHeader
              defaultActions={defaultActions}
              defaultActionsMenuItems={
                defaultActionsMenuItems ?? [
                  { label: "Report" },
                  { label: "Copy" },
                ]
              }
              headerActions={headerActions}
              leftSidebarOpen={leftSidebarOpen}
              onLeftSidebarChange={handleLeftSidebarChange}
              showDetail={showDetail}
              toggleLabel={toggleLabel}
            />

            <div className="flex flex-1 flex-col overflow-auto">
              {showDetail ? (
                detailViewWithProps
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  {emptyStateMessage}
                </div>
              )}
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </>
  );
}
